import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Tv, Lock, Unlock, Play, Pause, Coins, AlertCircle, 
  ExternalLink, ShieldCheck, Loader2, RefreshCw,
  Volume2, VolumeX
} from 'lucide-react'
import client from '../api/client'
import useBetStore from '../store/useBetStore'
import useAuthStore from '../store/useAuthStore'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import { useToast } from '../components/ui/Toast'
import Hls from 'hls.js'

// Utilidad para asegurar que las URLs de YouTube usen el formato de inserción (embed) y parámetros para una visualización más limpia
const formatStreamUrl = (url) => {
  if (!url) return '';
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId = null;
    try {
      // Expresión regular robusta para extraer el ID del video de YouTube (11 caracteres)
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
      const match = url.match(regExp);
      if (match && match[2].length === 11) {
        videoId = match[2];
      }
    } catch (e) {
      console.error("Error al extraer ID de YouTube:", e);
    }

    // Convertir a URL de inserción (embed) si logramos extraer el ID del video
    let finalUrl = url;
    if (videoId) {
      finalUrl = `https://www.youtube.com/embed/${videoId}`;
    }

    try {
      const urlObj = new URL(finalUrl);
      urlObj.searchParams.set('rel', '0');
      urlObj.searchParams.set('modestbranding', '1');
      urlObj.searchParams.set('iv_load_policy', '3');
      urlObj.searchParams.set('enablejsapi', '1');
      urlObj.searchParams.set('autoplay', '1');
      return urlObj.toString();
    } catch (e) {
      const separator = finalUrl.includes('?') ? '&' : '?';
      return `${finalUrl}${separator}rel=0&modestbranding=1&iv_load_policy=3&enablejsapi=1&autoplay=1`;
    }
  }
  return url;
};

// Utilidad de decodificación Base64 robusta con soporte para padding
const safeAtob = (str) => {
  if (!str) return '';
  let base64 = str.replace(/ /g, '+');
  while (base64.length % 4 !== 0) {
    base64 += '=';
  }
  try {
    return atob(base64);
  } catch (e) {
    console.error("Error al decodificar Base64:", e);
    return '';
  }
};

// Utilidad para extraer URL de DASH y claves DRM ClearKey del link de drm.php
const parseDrmUrl = (url) => {
  if (!url) return null;
  if (!url.includes('drm.php?url=')) return null;
  try {
    const urlObj = new URL(url);
    const urlParam = urlObj.searchParams.get('url');
    const kParam = urlObj.searchParams.get('k');
    if (urlParam && kParam) {
      const decodedUrl = safeAtob(urlParam);
      const decodedKeyStr = safeAtob(kParam);
      const parts = decodedKeyStr.split(':');
      if (parts.length === 2) {
        const [kid, key] = parts;
        return {
          streamUrl: decodedUrl,
          clearKeys: {
            [kid.replace(/-/g, '')]: key
          }
        };
      }
    }
  } catch (e) {
    console.error("Error al parsear URL de DRM:", e);
  }
  return null;
};

// Cargador de scripts dinámico
const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
};

export default function LiveStreaming() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const videoRef = useRef(null)
  const hlsRef = useRef(null)
  const shakaPlayerRef = useRef(null)
  
  const { credits, fetchBalance } = useBetStore()
  const { user } = useAuthStore()
  const isPremium = user?.premium === 1 || user?.rol === 'admin' || user?.rol === 'superadmin'

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })

  const nombreSistema = settings?.nombre_sistema || 'SubiGol'

  const [selectedChannel, setSelectedChannel] = useState(null)
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false)
  const [channelToPurchase, setChannelToPurchase] = useState(null)
  const [playerError, setPlayerError] = useState(false)
  const [cleanMode, setCleanMode] = useState(true)
  const [isIosLocked, setIsIosLocked] = useState(false)
  const [loadingShaka, setLoadingShaka] = useState(false)
  const [useIframeFallback, setUseIframeFallback] = useState(false)
  const [blockAds, setBlockAds] = useState(true)

  // Estados para Google Cast y AirPlay
  const [castAvailable, setCastAvailable] = useState(false)
  const [airPlayAvailable, setAirPlayAvailable] = useState(false)
  const [isCasting, setIsCasting] = useState(false)
  const [showCastHelpModal, setShowCastHelpModal] = useState(false)

  // Obtener la URL base dinámica de la aplicación para el instructivo de Smart TV
  const getAppBaseUrl = () => {
    const host = window.location.host;
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);
    
    // Rutas conocidas de la app para descartar
    const routesToDiscard = ['admin', 'login', 'register', 'transmisiones', 'partidos', 'finanzas', 'ajustes-saldo', 'reglas', 'terminos'];
    
    const baseSegments = segments.filter(seg => !routesToDiscard.includes(seg));
    const basePath = baseSegments.join('/');
    return basePath ? `${host}/${basePath}` : host;
  };

  // Inicialización de Google Cast y AirPlay
  useEffect(() => {
    window.__onGCastApiAvailable = function (isAvailable) {
      if (isAvailable && window.cast && window.cast.framework) {
        try {
          window.cast.framework.CastContext.getInstance().setOptions({
            receiverApplicationId: window.chrome.cast.media.DEFAULT_RECEIVER_APPLICATION_ID,
            autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
          });
          setCastAvailable(true);
        } catch (e) {
          console.error("Error al inicializar CastContext:", e);
        }
      }
    };

    loadScript('https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1')
      .catch(err => {
        console.warn("No se pudo cargar el script de Google Cast:", err);
      });
      
    // Detectar si AirPlay está disponible en Safari
    if (window.WebKitPlaybackTargetAvailabilityEvent) {
      setAirPlayAvailable(true);
    }
  }, []);

  // Escuchar cambios de estado en Chromecast
  useEffect(() => {
    if (castAvailable && window.cast && window.cast.framework) {
      try {
        const castContext = window.cast.framework.CastContext.getInstance();
        const castStateListener = (event) => {
          setIsCasting(event.castState === window.cast.framework.CastState.CONNECTED);
        };
        castContext.addEventListener(
          window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
          castStateListener
        );
        
        // Sincronizar estado inicial
        setIsCasting(castContext.getCastState() === window.cast.framework.CastState.CONNECTED);

        return () => {
          castContext.removeEventListener(
            window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
            castStateListener
          );
        };
      } catch (e) {
        console.warn("Error al agregar listener de CastState:", e);
      }
    }
  }, [castAvailable]);

  const handleCastClick = () => {
    if (!window.cast || !window.cast.framework) {
      toast.error("El servicio de Google Cast no está listo o no es compatible con este navegador.");
      return;
    }
    
    const castContext = window.cast.framework.CastContext.getInstance();
    
    // Obtener URL de transmisión
    let streamUrl = activeChannel?.stream_url;
    if (!streamUrl) return;
    
    // Si es una URL DRM codificada, intentar descifrarla
    if (isDrmUrl && drmConfig) {
      streamUrl = drmConfig.streamUrl;
    }
    
    castContext.requestSession()
      .then(session => {
        setIsCasting(true);
        
        // Crear información de media
        const contentType = streamUrl.includes('.mpd') ? 'application/dash+xml' : 'application/x-mpegURL';
        const mediaInfo = new window.chrome.cast.media.MediaInfo(streamUrl, contentType);
        
        mediaInfo.metadata = new window.chrome.cast.media.GenericMediaMetadata();
        mediaInfo.metadata.title = activeChannel.nombre;
        mediaInfo.metadata.subtitle = "Mundial 2026 - En vivo";
        
        if (activeChannel.logo_url) {
          const fullLogo = activeChannel.logo_url.startsWith('/uploads') ? `${baseImgUrl}${activeChannel.logo_url}` : activeChannel.logo_url;
          mediaInfo.metadata.images = [{ url: fullLogo }];
        }
        
        const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
        session.loadMedia(request).then(
          () => {
            toast.success("¡Transmitiendo en tu Smart TV / Chromecast!");
            if (videoRef.current) {
              videoRef.current.pause();
            }
          },
          err => {
            console.error('Error al cargar media en Chromecast:', err);
            toast.error("No se pudo iniciar la reproducción en Chromecast.");
            setIsCasting(false);
          }
        );
      })
      .catch(err => {
        console.error('Cancelado o error en Cast Session:', err);
      });
  };

  const handleStopCastClick = () => {
    if (!window.cast || !window.cast.framework) return;
    try {
      const castContext = window.cast.framework.CastContext.getInstance();
      castContext.endCurrentSession(true);
      setIsCasting(false);
      toast.success("Transmisión de TV detenida.");
    } catch (e) {
      console.error(e);
    }
  };

  const handleAirPlayClick = () => {
    const video = videoRef.current;
    if (video && video.webkitShowPlaybackTargetPicker) {
      video.webkitShowPlaybackTargetPicker();
    } else {
      toast.error("AirPlay no está soportado en este navegador. Utiliza Safari.");
    }
  };

  const handleSmartTvHelpClick = () => {
    setShowCastHelpModal(true);
  };

  const iframeRef = useRef(null)
  const containerRef = useRef(null)
  const [isYtPlaying, setIsYtPlaying] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [volume, setVolume] = useState(100)
  const [isMuted, setIsMuted] = useState(false)

  // Resetear el estado de reproducción y volumen al cambiar de canal
  useEffect(() => {
    setIsYtPlaying(true)
    setIsMuted(false)
    setVolume(100)
  }, [selectedChannel])

  // Función para pausar/reproducir YouTube vía postMessage
  const togglePlayPauseYouTube = () => {
    const iframe = iframeRef.current
    if (!iframe) return
    const nextState = !isYtPlaying
    setIsYtPlaying(nextState)
    const command = nextState ? 'playVideo' : 'pauseVideo'
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func: command, args: '' }),
      '*'
    )
  }

  // Función para manejar el cambio de volumen y enviarlo al iframe de YouTube
  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value, 10)
    setVolume(newVolume)
    if (newVolume > 0 && isMuted) {
      setIsMuted(false)
    }
    const iframe = iframeRef.current
    if (!iframe) return
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func: 'setVolume', args: [newVolume] }),
      '*'
    )
    if (newVolume === 0) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'mute', args: '' }),
        '*'
      )
    } else {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'unMute', args: '' }),
        '*'
      )
    }
  }

  // Función para alternar silencio (mute/unmute) de YouTube
  const toggleMuteYouTube = () => {
    const iframe = iframeRef.current
    if (!iframe) return
    const nextMuted = !isMuted
    setIsMuted(nextMuted)
    const command = nextMuted ? 'mute' : 'unMute'
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func: command, args: '' }),
      '*'
    )
    if (!nextMuted) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'setVolume', args: [volume] }),
        '*'
      )
    }
  }

  // Función para forzar la autoreproducción cuando el iframe de YouTube se monta y carga
  const handleIframeLoad = () => {
    if (!isYouTube || !cleanMode) return
    const iframe = iframeRef.current
    if (!iframe) return
    // Enviar comando para iniciar la reproducción automáticamente
    setTimeout(() => {
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'playVideo', args: '' }),
          '*'
        )
      }
    }, 1200) // Retardo para inicialización de YouTube
  }

  // Función para alternar pantalla completa sobre el contenedor del reproductor
  const handleToggleFullscreen = () => {
    const element = containerRef.current
    if (!element) return

    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (element.requestFullscreen) {
        element.requestFullscreen().then(() => setIsFullscreen(true)).catch(err => {
          console.error("Error al entrar en pantalla completa:", err)
        })
      } else if (element.webkitRequestFullscreen) { // Safari
        element.webkitRequestFullscreen()
        setIsFullscreen(true)
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {})
      } else if (document.webkitExitFullscreen) { // Safari
        document.webkitExitFullscreen()
        setIsFullscreen(false)
      }
    }
  }

  // Escuchar cambios de pantalla completa del navegador para rotar y restaurar la pantalla
  useEffect(() => {
    const handleFullscreenChange = () => {
      const activeElement = document.fullscreenElement || document.webkitFullscreenElement
      const isCurrentlyFullscreen = activeElement === containerRef.current
      setIsFullscreen(isCurrentlyFullscreen)

      // Intentar rotar la pantalla a horizontal (landscape) automáticamente en móviles al entrar a pantalla completa
      const orientation = window.screen?.orientation || window.screen?.mozOrientation || window.screen?.msOrientation;
      if (isCurrentlyFullscreen) {
        if (orientation && typeof orientation.lock === 'function') {
          orientation.lock('landscape').catch(err => {
            console.warn("No se pudo bloquear la orientación de la pantalla a horizontal:", err)
          })
        }
      } else {
        if (orientation && typeof orientation.unlock === 'function') {
          try {
            orientation.unlock()
          } catch (err) {
            console.warn("No se pudo liberar el bloqueo de orientación de la pantalla:", err)
          }
        }
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
    }
  }, [])

  // Obtener los canales activos
  const { data: channels = [], isLoading, isRefetching, refetch } = useQuery({
    queryKey: ['live-channels'],
    queryFn: async () => {
      const response = await client.get('/channels')
      return response.data?.datos || []
    }
  })

  // Evitar fallos de desestructuración nula o indefinida en recarga de página (F5)
  const channelsList = Array.isArray(channels) ? channels : []

  // Obtener el canal seleccionado actualizado
  const activeChannel = channelsList.find(c => c.id === selectedChannel?.id) || selectedChannel
  const isYouTube = activeChannel?.stream_url?.includes('youtube.com') || activeChannel?.stream_url?.includes('youtu.be')
  const drmConfig = parseDrmUrl(activeChannel?.stream_url)
  const isDrmUrl = drmConfig !== null
  const supportsSandbox = !activeChannel?.stream_url || !activeChannel.stream_url.includes('latamvidzfy.org')

  // Cada vez que los canales se actualicen, actualizamos el canal activo por si cambió su estado de acceso
  useEffect(() => {
    if (selectedChannel && channelsList.length > 0) {
      const updated = channelsList.find(c => c.id === selectedChannel.id)
      if (updated) {
        setSelectedChannel(updated)
      }
    }
  }, [channels])

  // Inicializar reproductores (HLS y DASH DRM) cuando cambia el canal activo o se desbloquea
  useEffect(() => {
    setPlayerError(false)
    setIsIosLocked(false)
    setLoadingShaka(false)
    setUseIframeFallback(false)
    
    // Destruir instancia anterior de HLS si existe
    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }

    // Destruir instancia anterior de Shaka Player si existe
    const destroyShaka = async () => {
      if (shakaPlayerRef.current) {
        try {
          await shakaPlayerRef.current.destroy()
        } catch (e) {
          console.warn("Error al destruir Shaka Player:", e)
        }
        shakaPlayerRef.current = null
      }
    }
    destroyShaka()

    const video = videoRef.current
    if (!video || !activeChannel || !activeChannel.tiene_acceso) {
      return
    }

    const streamUrl = activeChannel.stream_url
    if (!streamUrl) {
      setPlayerError(true)
      return
    }

    // Caso A: Canal DRM (DASH encriptado con ClearKey)
    if (isDrmUrl && !useIframeFallback) {
      const isIphone = /iPhone|iPad|iPod/i.test(navigator.userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      if (isIphone) {
        setIsIosLocked(true)
        return
      }

      const initShaka = async () => {
        setLoadingShaka(true)
        try {
          // Cargar script de Shaka Player si no está presente
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/shaka-player/4.3.5/shaka-player.compiled.js')
          
          // Verificar si el script cargó el objeto shaka global
          if (typeof window.shaka === 'undefined') {
            throw new Error("Shaka Player no pudo cargarse desde CDN")
          }

          // Instalar polyfills
          window.shaka.polyfill.installAll()

          if (window.shaka.Player.isBrowserSupported()) {
            const player = new window.shaka.Player(video)
            shakaPlayerRef.current = player

            player.addEventListener('error', (event) => {
              console.error('Error de Shaka Player:', event.detail)
              // Caída controlada a iframe (ej. por bloqueos de CORS externos o fallos del decodificador)
              setUseIframeFallback(true)
              setLoadingShaka(false)
            })

            // Configurar llaves DRM
            player.configure({
              drm: {
                clearKeys: drmConfig.clearKeys
              }
            })

            await player.load(drmConfig.streamUrl)
            setLoadingShaka(false)
            
            video.play().catch(err => {
              console.warn("Autoplay bloqueado por el navegador:", err)
            })
          } else {
            console.error('Shaka Player no es soportado por el navegador, usando fallback.')
            setUseIframeFallback(true)
            setLoadingShaka(false)
          }
        } catch (error) {
          console.error('Error al iniciar Shaka Player, activando fallback de iframe:', error)
          setUseIframeFallback(true)
          setLoadingShaka(false)
        }
      }

      initShaka()
      return
    }

    // Caso B: Canal HLS tradicional
    if (activeChannel.tipo === 'hls') {
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 90
        })
        hlsRef.current = hls
        hls.loadSource(streamUrl)
        hls.attachMedia(video)
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(err => {
            console.warn("Autoplay bloqueado por el navegador:", err)
          })
        })

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error("Error fatal de red en HLS, intentando recuperar...")
                hls.startLoad()
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error("Error fatal de medios en HLS, intentando recuperar...")
                hls.recoverMediaError()
                break
              default:
                console.error("Error fatal de HLS irrecuperable.")
                setPlayerError(true)
                hls.destroy()
                hlsRef.current = null
                break
            }
          }
        })
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Soporte nativo para Safari/iOS
        video.src = streamUrl
        video.addEventListener('loadedmetadata', () => {
          video.play().catch(err => {
            console.warn("Autoplay bloqueado por el navegador:", err)
          })
        })
        video.addEventListener('error', () => {
          setPlayerError(true)
        })
      } else {
        setPlayerError(true)
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
      if (shakaPlayerRef.current) {
        shakaPlayerRef.current.destroy().catch(() => {})
        shakaPlayerRef.current = null
      }
    }
  }, [activeChannel])

  // Mutación para realizar la compra del canal
  const purchaseMutation = useMutation({
    mutationFn: async (channelId) => {
      const response = await client.post(`/channels/${channelId}/purchase`)
      return response.data
    },
    onSuccess: (data) => {
      toast.success(data?.mensaje || 'Transmisión desbloqueada correctamente.')
      setPurchaseModalOpen(false)
      setChannelToPurchase(null)
      // Actualizar balance de créditos
      fetchBalance().catch(() => {})
      // Refrescar lista de canales para reflejar tiene_acceso: true
      queryClient.invalidateQueries({ queryKey: ['live-channels'] })
    },
    onError: (error) => {
      const msg = error.response?.data?.mensaje || 'Error al procesar la compra.'
      toast.error(msg)
    }
  })

  const handleSelectChannel = (channel) => {
    setSelectedChannel(channel)
  }

  const handleOpenPurchase = (channel) => {
    setChannelToPurchase(channel)
    setPurchaseModalOpen(true)
  }

  const handleConfirmPurchase = () => {
    if (channelToPurchase) {
      purchaseMutation.mutate(channelToPurchase.id)
    }
  }

  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 animate-fade-in font-body">
      {/* Encabezado */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary flex items-center gap-2.5">
            <Tv className="w-8 h-8 text-cyan animate-pulse" /> Partidos en Vivo
          </h1>
        </div>

        {/* Estatus Premium / Balance */}
        <div className="flex items-center gap-3">
          {isPremium ? (
            <div className="glass px-4 py-2 rounded-xl border border-success/30 bg-success/5 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-success" />
              <span className="text-xs font-semibold text-success font-display">Acceso Premium Libre</span>
            </div>
          ) : (
            <div className="glass px-4 py-2 rounded-xl border border-border-subtle flex items-center gap-3">
              <Coins className="w-4 h-4 text-cyan" />
              <div>
                <p className="text-[10px] text-text-muted font-medium uppercase leading-none">Mi Saldo</p>
                <p className="text-sm font-bold text-cyan mt-0.5">{credits.toFixed(2)} Bs</p>
              </div>
            </div>
          )}
          
          <button
            onClick={() => refetch()}
            disabled={isLoading || isRefetching}
            className="p-2.5 rounded-xl bg-surface-card border border-border-subtle hover:border-cyan/40 hover:bg-cyan/5 transition-all text-text-muted hover:text-cyan disabled:opacity-50 shrink-0"
            title="Actualizar transmisiones"
          >
            <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin text-cyan' : ''}`} />
          </button>
        </div>
      </div>

      {/* Grid Principal */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Lado izquierdo: Reproductor (col-span 3) */}
        <div className="lg:col-span-3 space-y-4">
          {/* Banner de Advertencia Legal DMCA (ancho del reproductor) */}
          <div className="border border-warning/30 bg-warning/5 px-4 py-3 rounded-2xl flex items-start gap-3 shadow-md">
            <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <p className="text-xs leading-relaxed text-text-secondary font-medium">
              Este contenido se muestra desde un proveedor externo mediante iframe. <strong className="text-text-primary font-semibold">{nombreSistema}</strong> no aloja ni transmite directamente esta señal. Si eres titular de derechos y deseas reportar material, revisa nuestra <Link to="/dmca" className="text-cyan hover:underline font-bold">política DMCA</Link>.
            </p>
          </div>

          <div 
            ref={containerRef}
            className="overflow-hidden border border-border-subtle bg-black relative aspect-video flex flex-col justify-center items-center shadow-2xl rounded-2xl isolate w-full"
          >
            {!activeChannel ? (
              // Estado inicial sin canal
              <div className="text-center p-8 text-text-muted">
                <Tv className="w-16 h-16 mx-auto mb-4 text-cyan/30 animate-bounce" />
                <h3 className="font-display font-semibold text-lg text-text-primary mb-1">
                  Ninguna transmisión seleccionada
                </h3>
                <p className="text-sm max-w-sm mx-auto">
                  Selecciona un canal de la lista lateral para sintonizar los encuentros en vivo y disfrutar del Mundial.
                </p>
              </div>
            ) : !activeChannel.tiene_acceso ? (
              // Bloqueado para compra
              <div className="absolute inset-0 bg-navy-dark/95 backdrop-blur-md flex flex-col justify-center items-center p-6 text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-warning/15 flex items-center justify-center text-warning border border-warning/20 shadow-lg shadow-warning/5 animate-pulse">
                  <Lock className="w-8 h-8" />
                </div>
                
                <div className="space-y-1">
                  <h3 className="font-display font-bold text-xl text-text-primary">
                    Canal Bloqueado: {activeChannel.nombre}
                  </h3>
                  <p className="text-sm text-text-secondary max-w-md mx-auto">
                    Esta transmisión premium está reservada para usuarios Premium o requiere una compra de acceso único.
                  </p>
                </div>

                <div className="glass px-6 py-3 rounded-2xl border border-border-subtle max-w-xs w-full flex justify-between items-center bg-surface-card/30">
                  <span className="text-xs text-text-muted font-medium">Costo de desbloqueo:</span>
                  <span className="font-display font-bold text-cyan text-base">{parseFloat(activeChannel.costo).toFixed(2)} Bs</span>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button 
                    onClick={() => handleOpenPurchase(activeChannel)}
                    variant="primary"
                    className="flex items-center gap-2"
                  >
                    <Coins className="w-4 h-4" /> Desbloquear Transmisión
                  </Button>
                  <Button 
                    onClick={() => setSelectedChannel(null)}
                    variant="outline"
                  >
                    Volver
                  </Button>
                </div>
              </div>
            ) : playerError ? (
              // Error de reproducción
              <div className="text-center p-8 text-text-muted space-y-4">
                <AlertCircle className="w-16 h-16 mx-auto text-error opacity-80" />
                <div>
                  <h3 className="font-display font-semibold text-lg text-text-primary mb-1">
                    Error al cargar la transmisión
                  </h3>
                  <p className="text-sm max-w-sm mx-auto">
                    No se pudo establecer conexión con la señal. El canal podría estar temporalmente fuera de línea.
                  </p>
                </div>
                <Button 
                  onClick={() => {
                    // Forzar recarga del canal
                    const current = activeChannel
                    setSelectedChannel(null)
                    setTimeout(() => setSelectedChannel(current), 100)
                  }}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 mx-auto"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Reintentar
                </Button>
              </div>
            ) : isIosLocked ? (
              // Bloqueo de DRM en iOS/iPhone
              <div className="absolute inset-0 bg-navy-dark/95 backdrop-blur-md flex flex-col justify-center items-center p-6 text-center space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-error/15 flex items-center justify-center text-error border border-error/20 shadow-lg shadow-error/5 animate-pulse">
                  <AlertCircle className="w-8 h-8" />
                </div>
                
                <div className="space-y-1">
                  <h3 className="font-display font-bold text-xl text-text-primary">
                    Transmisión no Soportada en iOS
                  </h3>
                  <p className="text-sm text-text-secondary max-w-sm mx-auto">
                    Esta señal utiliza cifrado **DRM Widevine**, el cual no es compatible con el navegador Safari de tu iPhone por limitaciones de Apple.
                  </p>
                  <p className="text-xs text-text-muted max-w-xs mx-auto">
                    Para visualizar este canal, por favor utiliza un dispositivo Android o una computadora con Chrome, Firefox o Edge.
                  </p>
                </div>
              </div>
            ) : activeChannel.tipo === 'iframe' && (!isDrmUrl || useIframeFallback) ? (
              // Reproductor Iframe (Sandboxed para seguridad contra anuncios intrusivos)
              <div className="w-full h-full relative overflow-hidden rounded-2xl">
                <iframe
                  ref={iframeRef}
                  src={formatStreamUrl(activeChannel.stream_url)}
                  onLoad={handleIframeLoad}
                  className="absolute border-0 transition-all duration-300"
                  style={{
                    top: isYouTube && cleanMode ? '-16%' : '0',
                    left: isYouTube && cleanMode ? '0' : '0',
                    width: isYouTube && cleanMode ? '100%' : '100%',
                    height: isYouTube && cleanMode ? '132%' : '100%',
                  }}
                  allowFullScreen
                  scrolling="no"
                  allow="autoplay; encrypted-media; picture-in-picture; presentation"
                  sandbox={isYouTube || (blockAds && parseInt(activeChannel?.permitir_bloqueo_ads) !== 0 && supportsSandbox) ? "allow-scripts allow-same-origin allow-presentation allow-forms" : undefined}
                  title={activeChannel.nombre}



                />
                {isYouTube && cleanMode && (
                  <div 
                    className="absolute inset-0 z-10 cursor-pointer bg-transparent"
                    onClick={togglePlayPauseYouTube}
                    onDoubleClick={handleToggleFullscreen}
                    title="Hacer clic para pausar/reproducir, doble clic para pantalla completa"
                  />
                )}
                {isYouTube && cleanMode && (
                  <div className="absolute bottom-4 right-4 z-20 flex items-center gap-3 px-3 py-2 rounded-xl bg-black/40 border border-white/10 backdrop-blur-md text-white transition-all">
                    {/* Botón Play/Pause */}
                    <button
                      onClick={togglePlayPauseYouTube}
                      className="p-1.5 rounded-lg hover:bg-white/10 text-white transition-all hover:scale-105"
                      title={isYtPlaying ? "Pausar" : "Reproducir"}
                    >
                      {isYtPlaying ? (
                        <Pause className="w-4 h-4 text-white" />
                      ) : (
                        <Play className="w-4 h-4 text-cyan" />
                      )}
                    </button>

                    {/* Control de Volumen */}
                    <div className="flex items-center gap-1.5 group/vol">
                      <button
                        onClick={toggleMuteYouTube}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-white transition-all hover:scale-105"
                        title={isMuted || volume === 0 ? "Activar sonido" : "Silenciar"}
                      >
                        {isMuted || volume === 0 ? (
                          <VolumeX className="w-4 h-4 text-error" />
                        ) : (
                          <Volume2 className="w-4 h-4 text-white" />
                        )}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={isMuted ? 0 : volume}
                        onChange={handleVolumeChange}
                        className="w-0 group-hover/vol:w-16 h-1 bg-white/20 accent-cyan rounded-lg appearance-none cursor-pointer transition-all duration-300 ml-0.5 focus:outline-none"
                        style={{
                          WebkitAppearance: 'none'
                        }}
                      />
                    </div>

                    <div className="w-[1px] h-4 bg-white/10" />

                    {/* Botón Pantalla Completa */}
                    <button
                      onClick={handleToggleFullscreen}
                      className="p-1.5 rounded-lg hover:bg-white/10 text-white transition-all hover:scale-105"
                      title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
                    >
                      {isFullscreen ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7"/></svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3"/></svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Reproductor HLS nativo + Hls.js o DASH DRM nativo (Mantiene el tag <video> montado al cargar)
              <div className="w-full h-full relative">
                <video
                  ref={videoRef}
                  className="w-full h-full object-contain"
                  controls
                  playsInline
                  preload="auto"
                  x-webkit-airplay="allow"
                />
                {loadingShaka && (
                  <div className="absolute inset-0 bg-navy-dark/95 backdrop-blur-md flex flex-col justify-center items-center p-6 text-center space-y-3 z-20">
                    <Loader2 className="w-10 h-10 text-cyan animate-spin" />
                    <p className="text-sm font-medium text-text-secondary">Inicializando decodificador DRM seguro...</p>
                  </div>
                )}
              </div>
            )}

            {/* Pantalla de Casting Activo */}
            {isCasting && (
              <div className="absolute inset-0 bg-navy-dark/95 backdrop-blur-md flex flex-col justify-center items-center p-6 text-center space-y-4 z-30 animate-fade-in">
                <div className="w-16 h-16 rounded-2xl bg-cyan/15 flex items-center justify-center text-cyan border border-cyan/20 shadow-lg shadow-cyan/5 animate-pulse">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan">
                    <path d="M2 16.1A5 5 0 0 1 5.9 20M2 12.05A9 9 0 0 1 9.95 20M2 8A13 13 0 0 1 14 20M2 20h.01"/>
                    <rect width="20" height="15" x="2" y="3" rx="2"/>
                  </svg>
                </div>
                
                <div className="space-y-1">
                  <h3 className="font-display font-bold text-xl text-text-primary">
                    Transmitiendo en Smart TV
                  </h3>
                  <p className="text-sm text-text-secondary max-w-sm mx-auto">
                    El partido se está reproduciendo en tu televisor. Controla la reproducción desde aquí o detén la transmisión.
                  </p>
                </div>
                
                <Button 
                  onClick={handleStopCastClick}
                  variant="outline"
                  className="flex items-center gap-2 border-error/30 text-error hover:bg-error/10 hover:border-error/50"
                >
                  Detener Transmisión
                </Button>
              </div>
            )}
          </div>

          {/* Información del canal sintonizado */}
          {activeChannel && (
            <div className="space-y-4">
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 glass rounded-2xl border border-border-subtle"
              >
                <div className="flex items-center gap-3">
                  {activeChannel.logo_url ? (
                    <div className="w-12 h-12 rounded-xl bg-navy-dark border border-border-subtle p-1 flex items-center justify-center shrink-0">
                      <img 
                        src={activeChannel.logo_url.startsWith('/uploads') ? `${baseImgUrl}${activeChannel.logo_url}` : activeChannel.logo_url} 
                        alt={activeChannel.nombre} 
                        className="max-h-full max-w-full object-contain rounded"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-cyan/20 to-indigo-500/20 text-cyan border border-cyan/20 flex items-center justify-center shrink-0 font-display font-bold text-lg">
                      {activeChannel.nombre.substring(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h2 className="font-display font-bold text-lg text-text-primary">
                      Sintonizando: {activeChannel.nombre}
                    </h2>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan/15 text-cyan uppercase tracking-wider">
                        {activeChannel.tipo === 'hls' ? 'Señal HLS Directa' : 'Inserción Iframe'}
                      </span>
                      {activeChannel.tiene_acceso && parseFloat(activeChannel.costo) > 0 && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-success/15 text-success border border-success/10 flex items-center gap-1">
                          <Unlock className="w-2.5 h-2.5" /> Adquirido permanentemente
                        </span>
                      )}
                      {parseFloat(activeChannel.costo) === 0 && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-green-500/10 text-green-400 border border-green-500/10">
                          Acceso Libre Gratuito
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Controles del Reproductor (Derecha) */}
                {activeChannel && activeChannel.tipo === 'iframe' && (
                  <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto self-stretch justify-end sm:self-center">
                    
                    {/* Antianuncios Toggle */}
                    {parseInt(activeChannel?.permitir_bloqueo_ads) !== 0 && (
                      supportsSandbox ? (
                        <button
                          type="button"
                          onClick={() => setBlockAds(!blockAds)}
                          className={`
                            flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all duration-200 cursor-pointer
                            ${blockAds
                              ? 'bg-success/10 text-success border-success/35 hover:bg-success/20 shadow-sm shadow-success/5'
                              : 'bg-text-muted/10 text-text-secondary border-border-default hover:bg-text-muted/20'
                            }
                          `}
                          title={blockAds ? "Desactiva el bloqueador si la señal muestra pantalla negra o error" : "Activa el bloqueador para evitar pestañas emergentes y anuncios"}
                        >
                          <ShieldCheck className={`w-4 h-4 ${blockAds ? 'animate-pulse' : ''}`} />
                          <span>{blockAds ? 'Antianuncios: Sí' : 'Antianuncios: No'}</span>
                        </button>
                      ) : (
                        <div
                          className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border bg-error/5 text-error/80 border-error/20"
                          title="Esta señal no es compatible con el bloqueador de anuncios por protección anti-sandbox del proveedor"
                        >
                          <AlertCircle className="w-4 h-4" />
                          <span>Antianuncios: No compatible</span>
                        </div>
                      )
                    )}

                    {/* Abrir en pestaña externa */}
                    {parseInt(activeChannel?.permitir_enlace_externo) !== 0 && (
                      <a
                        href={activeChannel.stream_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-navy-light hover:bg-navy-light/80 border border-border-subtle hover:border-border-default text-text-primary transition-all duration-200"
                        title="Abre la señal directamente en tu navegador si la carga dentro de la app falla por restricciones CORS"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        <span>Abrir Señal</span>
                      </a>
                    )}

                  </div>
                )}
              </motion.div>

              {/* Panel de Transmisión a Smart TV */}
              {activeChannel.tiene_acceso && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 glass rounded-2xl border border-border-subtle bg-surface-card/10 flex flex-col md:flex-row justify-between items-center gap-4 animate-fade-in"
                >
                  <div className="flex items-center gap-3">
                    <Tv className="w-5 h-5 text-cyan animate-pulse" />
                    <div>
                      <p className="text-sm font-semibold text-text-primary">¿Quieres verlo en pantalla grande?</p>
                      <p className="text-xs text-text-muted">Transmite la señal en vivo a tu Smart TV, Chromecast, Fire TV o Apple TV.</p>
                    </div>
                  </div>

                  {isCasting ? (
                    <div className="flex items-center gap-3">
                      <span className="flex h-2 w-2 relative">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                      </span>
                      <span className="text-xs font-semibold text-success">Transmitiendo en TV...</span>
                      <Button 
                        onClick={handleStopCastClick} 
                        variant="outline" 
                        size="sm" 
                        className="text-xs border-error/30 hover:bg-error/10 hover:text-error py-1.5 px-3"
                      >
                        Detener Transmisión
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Botón de Google Cast (Chromecast) */}
                      {activeChannel.tipo === 'hls' ? (
                        <>
                          <button
                            onClick={handleCastClick}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-border-subtle bg-surface-card hover:border-cyan/50 hover:bg-cyan/5 text-xs font-semibold text-text-secondary hover:text-cyan transition-all cursor-pointer"
                            title="Enviar transmisión a Chromecast o Android TV"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan">
                              <path d="M2 16.1A5 5 0 0 1 5.9 20M2 12.05A9 9 0 0 1 9.95 20M2 8A13 13 0 0 1 14 20M2 20h.01"/>
                              <rect width="20" height="15" x="2" y="3" rx="2"/>
                            </svg>
                            Transmitir (Chromecast)
                          </button>
                          
                          {/* Botón de AirPlay */}
                          {airPlayAvailable && (
                            <button
                              onClick={handleAirPlayClick}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-border-subtle bg-surface-card hover:border-indigo-500/50 hover:bg-indigo-500/5 text-xs font-semibold text-text-secondary hover:text-indigo-400 transition-all cursor-pointer"
                              title="Enviar transmisión a Apple TV o televisor con AirPlay"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-indigo-400">
                                <path d="M5 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-1"/>
                                <polygon points="12 15 17 21 7 21 12 15"/>
                              </svg>
                              Transmitir (AirPlay)
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="text-[10px] text-text-muted bg-navy-light px-2.5 py-1.5 rounded-xl border border-border-subtle mr-1">
                          Las transmisiones iframe deben ser compartidas desde su propio menú o navegador.
                        </div>
                      )}

                      {/* Botón Instructivo / Genérico Smart TV */}
                      <button
                        onClick={handleSmartTvHelpClick}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-border-subtle bg-surface-card hover:border-warning/50 hover:bg-warning/5 text-xs font-semibold text-text-secondary hover:text-warning transition-all cursor-pointer"
                        title="Cómo ver en televisores sin soporte nativo de Chromecast/AirPlay"
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-warning" />
                        Otros Smart TV / Fire TV
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          )}
        </div>

        {/* Lado derecho: Lista de canales (col-span 1) */}
        <div className="lg:col-span-1 space-y-4">
          <h3 className="font-display font-bold text-lg text-text-primary px-1">
            Canales Disponibles ({channelsList.length})
          </h3>
          
          {isLoading ? (
            <div className="space-y-3">
              <div className="h-16 rounded-xl bg-surface-card/40 animate-pulse border border-border-subtle" />
              <div className="h-16 rounded-xl bg-surface-card/40 animate-pulse border border-border-subtle" />
              <div className="h-16 rounded-xl bg-surface-card/40 animate-pulse border border-border-subtle" />
            </div>
          ) : channelsList.length === 0 ? (
            <Card className="p-6 text-center text-text-muted border border-border-subtle bg-surface-card/20">
              <Tv className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No hay canales de transmisión disponibles en este momento.</p>
            </Card>
          ) : (
            <div className="space-y-2.5 max-h-[60vh] lg:max-h-[70vh] overflow-y-auto pr-1">
              {channelsList.map((channel) => {
                const isSelected = selectedChannel?.id === channel.id
                const isPaid = parseFloat(channel.costo) > 0
                const locked = !channel.tiene_acceso

                return (
                  <button
                    key={channel.id}
                    onClick={() => handleSelectChannel(channel)}
                    className={`
                      w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all duration-200
                      ${isSelected
                        ? 'bg-cyan/10 border-cyan text-text-primary shadow-lg shadow-cyan/5'
                        : 'bg-surface-card/45 hover:bg-surface-card border-border-subtle hover:border-border-default'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Logo del canal */}
                      {channel.logo_url ? (
                        <div className="w-10 h-10 rounded-lg bg-navy-dark border border-border-subtle p-1 flex items-center justify-center shrink-0">
                          <img 
                            src={channel.logo_url.startsWith('/uploads') ? `${baseImgUrl}${channel.logo_url}` : channel.logo_url} 
                            alt={channel.nombre} 
                            className="max-h-full max-w-full object-contain rounded"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-tr from-cyan/10 to-indigo-500/10 text-cyan border border-cyan/10 flex items-center justify-center shrink-0 font-display font-bold text-sm">
                          {channel.nombre.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      
                      {/* Nombre y tipo */}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate text-text-primary">
                          {channel.nombre}
                        </p>
                        <p className="text-[10px] text-text-secondary capitalize">
                          {channel.tipo === 'hls' ? 'Señal HLS' : 'Inserción'}
                        </p>
                      </div>
                    </div>

                    {/* Costo / Lock status */}
                    <div className="text-right shrink-0 ml-2">
                      {locked ? (
                        <span className="flex items-center gap-1 text-[11px] font-bold text-warning bg-warning/10 border border-warning/20 px-2 py-0.5 rounded">
                          <Lock className="w-3 h-3" /> {parseFloat(channel.costo).toFixed(0)} Bs
                        </span>
                      ) : isPaid ? (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-success bg-success/10 border border-success/20 px-2 py-0.5 rounded">
                          <Unlock className="w-2.5 h-2.5" /> Libre
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-green-400 bg-green-500/10 border border-green-500/20 px-2 py-0.5 rounded">
                          Gratis
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal Confirmación de Compra */}
      <AnimatePresence>
        {purchaseModalOpen && channelToPurchase && (
          <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white dark:bg-navy-dark max-w-md w-full rounded-2xl overflow-hidden shadow-2xl border border-border-default"
            >
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3 text-cyan">
                  <Coins className="w-6 h-6 text-cyan animate-bounce" />
                  <h3 className="font-display font-bold text-lg text-text-primary">
                    Confirmar Desbloqueo de Canal
                  </h3>
                </div>

                <div className="space-y-2 text-sm text-text-secondary">
                  <p>
                    Estás por adquirir acceso ilimitado y permanente al canal de transmisión <strong>{channelToPurchase.nombre}</strong>.
                  </p>
                  <p className="text-xs">
                    El costo será debitado de tu saldo y se registrará un movimiento contable en tu historial.
                  </p>
                </div>

                {/* Detalles de la transacción */}
                <div className="p-4 bg-surface-card/40 border border-border-subtle rounded-xl space-y-2.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-muted">Costo del canal:</span>
                    <span className="font-bold text-text-primary">{parseFloat(channelToPurchase.costo).toFixed(2)} Bs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Tu saldo actual:</span>
                    <span className="font-bold text-text-primary">{credits.toFixed(2)} Bs</span>
                  </div>
                  <hr className="border-border-subtle" />
                  <div className="flex justify-between">
                    <span className="text-text-muted">Saldo resultante:</span>
                    <span className={`font-bold ${(credits - channelToPurchase.costo) >= 0 ? 'text-cyan' : 'text-error'}`}>
                      {(credits - channelToPurchase.costo).toFixed(2)} Bs
                    </span>
                  </div>
                </div>

                {/* Validación de fondos */}
                {credits < channelToPurchase.costo && (
                  <div className="p-3 bg-error/10 border border-error/20 rounded-xl text-error text-xs flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>No tienes saldo suficiente para comprar este canal. Por favor, realiza una recarga de créditos.</span>
                  </div>
                )}

                {/* Botones */}
                <div className="pt-2 flex justify-end gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setPurchaseModalOpen(false)
                      setChannelToPurchase(null)
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="button" 
                    variant="primary" 
                    onClick={handleConfirmPurchase}
                    loading={purchaseMutation.isPending}
                    disabled={credits < channelToPurchase.costo}
                  >
                    Confirmar Pago
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal Ayuda de Casting para otros Smart TV */}
      <AnimatePresence>
        {showCastHelpModal && (
          <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white dark:bg-navy-dark max-w-md w-full rounded-2xl overflow-hidden shadow-2xl border border-border-default font-body"
            >
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3 text-cyan">
                  <Tv className="w-6 h-6 text-cyan animate-bounce" />
                  <h3 className="font-display font-bold text-lg text-text-primary">
                    Transmitir a Smart TV / Fire TV
                  </h3>
                </div>

                <div className="space-y-3 text-sm text-text-secondary">
                  <p>
                    Si tu televisor no aparece directamente en las opciones de transmisión nativa, puedes usar los siguientes métodos alternativos:
                  </p>
                  
                  <div className="space-y-2.5 pt-1">
                    <div className="p-3 bg-surface-card/40 border border-border-subtle rounded-xl text-xs space-y-1">
                      <p className="font-bold text-text-primary">Método 1: Navegador de la TV (Recomendado)</p>
                      <p>Abre el navegador de internet de tu Smart TV (LG WebOS, Samsung Tizen, Android TV, etc.) e ingresa a nuestro sitio:</p>
                      <p className="font-mono text-cyan text-center font-bold bg-black/10 py-1 rounded select-all">{getAppBaseUrl()}</p>
                    </div>

                    <div className="p-3 bg-surface-card/40 border border-border-subtle rounded-xl text-xs space-y-1">
                      <p className="font-bold text-text-primary">Método 2: Web Video Caster (Móvil)</p>
                      <p>Instala la aplicación gratuita **Web Video Caster** en tu teléfono celular. Abre la app, escribe la dirección de nuestro sitio y conéctala a tu TV Stick (Fire TV, Roku, Chromecast, etc.) para transmitir la señal directa.</p>
                    </div>

                    <div className="p-3 bg-surface-card/40 border border-border-subtle rounded-xl text-xs space-y-1">
                      <p className="font-bold text-text-primary">Método 3: Duplicación de Pantalla</p>
                      <p>Usa la opción nativa de "Transmitir pantalla" (Smart View en Samsung, Cast en Xiaomi/Android) o "Duplicar pantalla" (AirPlay en iOS) de tu celular para reflejarlo en la TV.</p>
                    </div>
                  </div>
                </div>

                <div className="pt-2 flex justify-end">
                  <Button 
                    type="button" 
                    variant="primary" 
                    onClick={() => setShowCastHelpModal(false)}
                  >
                    Entendido
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
